"use client";

import { useEffect, useRef } from "react";
import gsap from "gsap";
import { HeroBody } from "./HeroBody";
import { useReducedMotion } from "./useReducedMotion";
import type { HeroContent } from "./types";

const RINGS = [
  { size: "88%", duration: 48, dots: 3 },
  { size: "68%", duration: 36, dots: 4 },
  { size: "48%", duration: 28, dots: 5 },
] as const;

export function OrbitHero({ content }: { content: HeroContent }) {
  const rootRef = useRef<HTMLElement | null>(null);
  const hubRef = useRef<HTMLDivElement | null>(null);
  const ringRefs = useRef<(HTMLDivElement | null)[]>([]);
  const badgeRef = useRef<HTMLSpanElement | null>(null);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const subtitleRef = useRef<HTMLParagraphElement | null>(null);
  const ctasRef = useRef<HTMLDivElement | null>(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (!rootRef.current) return;
    const ctx = gsap.context(() => {
      const intro = [badgeRef.current, titleRef.current, subtitleRef.current, ctasRef.current].filter(Boolean);
      if (reduced) {
        gsap.set(intro, { opacity: 1, y: 0 });
        return;
      }
      gsap.set(intro, { opacity: 0, y: 26 });
      gsap.to(intro, {
        opacity: 1,
        y: 0,
        duration: 0.9,
        ease: "power3.out",
        stagger: 0.11,
        delay: 0.15,
      });

      if (hubRef.current) {
        gsap.to(hubRef.current, {
          scale: 1.06,
          opacity: 0.95,
          duration: 3.5,
          ease: "sine.inOut",
          repeat: -1,
          yoyo: true,
        });
      }

      ringRefs.current.forEach((el, i) => {
        if (!el) return;
        gsap.to(el, {
          rotation: 360,
          duration: RINGS[i]?.duration ?? 40,
          ease: "none",
          repeat: -1,
        });
      });
    }, rootRef);
    return () => ctx.revert();
  }, [reduced]);

  return (
    <section
      ref={rootRef}
      className="hero-section hero-grain relative isolate overflow-hidden bg-[var(--primary-darker)] px-4 py-20 md:py-28"
      aria-label="Hero"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/2 size-[min(92vw,52rem)] -translate-x-1/2 -translate-y-[42%]"
      >
        <div
          ref={hubRef}
          className="absolute left-1/2 top-1/2 size-32 -translate-x-1/2 -translate-y-1/2 rounded-full opacity-80 blur-2xl md:size-40"
          style={{
            background:
              "radial-gradient(circle, color-mix(in oklch, var(--accent) 70%, white) 0%, transparent 70%)",
          }}
        />
        {RINGS.map((ring, ri) => (
          <div
            key={ring.size}
            ref={(el) => {
              ringRefs.current[ri] = el;
            }}
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/[0.08]"
            style={{ width: ring.size, height: ring.size }}
          >
            {Array.from({ length: ring.dots }).map((_, di) => {
              const angle = (360 / ring.dots) * di;
              return (
                <span
                  key={di}
                  className="absolute inset-0"
                  style={{ transform: `rotate(${angle}deg)` }}
                >
                  <span className="absolute left-1/2 top-0 size-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--accent)] shadow-[0_0_12px_var(--accent)]" />
                </span>
              );
            })}
          </div>
        ))}
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background:
              "radial-gradient(circle at 50% 50%, transparent 35%, rgba(0,0,0,0.55) 100%)",
          }}
        />
      </div>

      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(to bottom, transparent 35%, rgba(0,0,0,0.5) 100%), radial-gradient(ellipse 80% 50% at 50% 20%, rgba(255,255,255,0.04) 0%, transparent 70%)",
        }}
      />

      <HeroBody
        content={content}
        primaryVariant="primary"
        secondaryVariant="outline"
        refs={{ badge: badgeRef, title: titleRef, subtitle: subtitleRef, ctas: ctasRef }}
      />
    </section>
  );
}
